// StudyScreen.jsx — "What are you working on?" composer.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

// Brand glyphs — sourced from simpleicons.org's single-color CDN, colored
// with each product's official brand hex. Real logos, no hand-drawn
// approximations. Lazy-loads; if CDN is unreachable the alt text shows.
function DriveIcon() {
  return (
    <img
      src="https://cdn.simpleicons.org/googledrive/0F9D58"
      alt="Drive"
      width="14"
      height="14"
      style={{ display: "block" }}
    />
  );
}
function CanvasIcon() {
  return (
    <img
      src="https://cdn.simpleicons.org/instructure/E72429"
      alt="Canvas"
      width="14"
      height="14"
      style={{ display: "block" }}
    />
  );
}

export function StudyScreen({ onIngestStart, onImported, onError }) {
  const [val, setVal] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [openConnect, setOpenConnect] = React.useState(null); // "drive" | "canvas" | null
  const fileRef = React.useRef(null);
  const connectRef = React.useRef(null);

  React.useEffect(() => {
    if (!openConnect) return;
    const close = (e) => {
      if (connectRef.current && !connectRef.current.contains(e.target)) setOpenConnect(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [openConnect]);

  const pickFile = () => fileRef.current?.click();

  const runIngest = async (fn, label) => {
    setBusy(true);
    onIngestStart?.(label);
    try {
      const result = await fn();
      onImported?.(result);
    } catch (e) {
      onError?.(e?.message || "Couldn't parse this. Try a syllabus PDF, a lecture outline, or a course description.");
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    runIngest(
      () => api.postIngestUpload(f, { context: val.trim() }),
      f.name,
    );
  };

  const handleText = () => {
    const content = val.trim();
    if (!content) return;
    runIngest(
      () => api.postIngestText(content),
      content.length > 40 ? content.slice(0, 40) + "…" : content,
    );
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleText();
    }
  };

  return (
    <section className="study">
      <h1 className="study-head">What are you working on?</h1>
      <div className="composer">
        <textarea
          className="composer-input"
          placeholder="Paste a syllabus, describe the class you're taking, or drop a PDF using the clip."
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
        />
        <div className="composer-bar">
          <div className="composer-actions">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.txt,.md"
              onChange={handleFile}
              style={{ display: "none" }}
            />
            <button
              className="attach-btn"
              title="Attach a syllabus or PDF"
              onClick={pickFile}
              disabled={busy}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M16 6l-8 8a3 3 0 004 4l9-9a5 5 0 00-7-7L4 12a7 7 0 0010 10"/>
              </svg>
              <span>Attach PDF</span>
            </button>
            <button
              className="attach-btn source-btn-drive"
              title="Import from Google Drive"
              onClick={(e) => { e.stopPropagation(); setOpenConnect(openConnect === "drive" ? null : "drive"); }}
              disabled={busy}
            >
              <DriveIcon />
              <span>Drive</span>
            </button>
            <button
              className="attach-btn source-btn-canvas"
              title="Import from Canvas LMS"
              onClick={(e) => { e.stopPropagation(); setOpenConnect(openConnect === "canvas" ? null : "canvas"); }}
              disabled={busy}
            >
              <CanvasIcon />
              <span>Canvas</span>
            </button>
          </div>
          <button
            className="send-btn"
            onClick={handleText}
            aria-label="send"
            disabled={busy || !val.trim()}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 19V5 M5 12l7-7 7 7"/>
            </svg>
          </button>
        </div>
        <div className="composer-hint">⌘ + Enter to send · PDF up to 5MB</div>

        {openConnect && (
          <div className="connect-pop" ref={connectRef}>
            <div className="connect-pop-head">
              {openConnect === "drive" ? <DriveIcon /> : <CanvasIcon />}
              <div className="connect-pop-title">
                {openConnect === "drive" ? "Connect Google Drive" : "Connect Canvas"}
              </div>
              <button className="connect-pop-close" onClick={() => setOpenConnect(null)}>✕</button>
            </div>
            <div className="connect-pop-body">
              {openConnect === "drive"
                ? "Sign in with Google to import syllabi, lecture slides, and notes directly from your Drive."
                : "Authorize Didactic with your school's Canvas LMS to pull your class list, syllabi, and weekly problem sets."}
            </div>
            <button
              className="btn-primary connect-pop-cta"
              onClick={() => { window.alert("Integration not wired up yet — use the Attach PDF button for now."); setOpenConnect(null); }}
            >
              {openConnect === "drive" ? "Continue with Google" : "Continue with Canvas"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

